export const en = {
  common: {
    loading: "Loading…",
    createAgent: "Create an agent",
    loadAgentsFailed: "Could not load agents.",
    toolCount_one: "{count} tool",
    toolCount_few: "{count} tools",
    toolCount_many: "{count} tools",
    toolCount_other: "{count} tools",
    enabled: "Enabled",
    disconnect: "Disconnect",
    connectedAs: "Connected as",
    disconnectFailed: "Could not disconnect.",
    cancel: "Cancel",
    contactAdmin: "Contact admin",
    errorTitle: "Something went wrong",
    errorBody: "The app hit an unexpected error. Reload the page, and if it keeps happening, contact the admin.",
    errorReload: "Reload",
    name: "Name",
    actionFailed: "That did not work.",
    expandAll: "Expand all",
    collapseAll: "Collapse all",
    showLess: "show less",
    showAllChars: "show all {count} chars",
    itemCount_one: "{count} item",
    itemCount_few: "{count} items",
    itemCount_many: "{count} items",
    itemCount_other: "{count} items",
    keyCount_one: "{count} key",
    keyCount_few: "{count} keys",
    keyCount_many: "{count} keys",
    keyCount_other: "{count} keys",
    status: {
      disabled: "Disabled",
      run: {
        queued: "Queued",
        running: "Running",
        succeeded: "Succeeded",
        failed: "Failed",
        cancelled: "Cancelled",
        maxIterations: "Hit step limit",
        timeout: "Timed out",
        abandoned: "Abandoned",
      },
      connection: {
        connected: "Connected",
        unreachable: "Unreachable",
        notChecked: "Not checked",
      },
    },
  },
  nav: {
    guide: "Guide",
    chat: "Chat",
    agents: "Agents",
    toolServers: "Tool servers",
    plugins: "Plugins",
    schedules: "Schedules",
    runs: "Runs",
    activity: "Activity",
    workspace: "Workspace",
    billing: "Billing",
    admin: "Admin",
    theme: "Theme",
    language: "Language",
    signOut: "Sign out",
    openMenu: "Open menu",
    closeMenu: "Close menu",
  },
  workspace: {
    switcherLabel: "Workspace",
    loadFailed: "Could not load workspaces.",
    new: "New workspace",
    newLabel: "Create workspace",
    dialogDescription: "Name your new workspace. You'll switch to it once it's created.",
    namePlaceholder: "Acme Inc.",
    create: "Create workspace",
    toastCreated: "Workspace created.",
    createFailed: "Could not create the workspace.",
  },
  guide: {
    title: "Guide",
    intro:
      "This is a place to build your own AI assistants — we call them **agents**. An agent chats with you, and you decide what it's for and which tools it's allowed to use. You can talk to it here on the web or from Telegram, and it can even work on a schedule while you're away.",
    useCases: {
      title: "What you can do",
      intro: "A few things people set their agents up to do:",
      usesLabel: "Uses: {name}",
      items: {
        spending: {
          title: "Track your spending",
          blurb: "Tell it what you spent and ask where your money went this month.",
          using: "Expense Tracker plugin",
        },
        email: {
          title: "Handle your email",
          blurb: "Have it read, summarise, and draft replies to messages in your inbox.",
          using: "Mail plugin",
        },
        calendar: {
          title: "Calendar & reminders",
          blurb: "Ask what's on today, or add an event by just describing it.",
          using: "Google Calendar plugin",
        },
        briefings: {
          title: "Scheduled briefings",
          blurb: "Get a daily summary sent to you every morning, all on its own.",
          using: "Schedules",
        },
        tools: {
          title: "Use your own tools",
          blurb: "Connect any tool server and the agent can call it while it works.",
          using: "Tool servers (MCP)",
        },
        telegram: {
          title: "Chat from Telegram",
          blurb: "Message your agent from your phone like any other Telegram chat.",
          using: "Telegram",
        },
      },
    },
    createAgent: {
      title: "Create your first agent",
      intro:
        "An agent is just three things: a model (the brain), your instructions (what it should do), and the tools you let it use. Setting one up takes a minute.",
      step1: "Go to **Agents** and click **New agent**.",
      step2: 'Give it a name — say, "Money helper" or "Email assistant".',
      step3:
        'Write what it should do in plain words, e.g. "You help me track what I spend and answer questions about my budget."',
      step4: "Turn on the tools it needs (see below), then save.",
      step5: "Open **Chat**, pick your agent, and say hello.",
      cta: "Create an agent",
    },
    tools: {
      title: "Give it tools",
      body:
        "Tools are what let an agent actually *do* things instead of only talking. The easiest are **plugins** — ready-made tools like the expense tracker, mail, and calendar that you switch on for an agent. For anything custom you can also connect a **tool server** of your own.",
      browsePlugins: "Browse plugins",
      connectServer: "Connect a tool server",
    },
    notion: {
      title: "Connect Notion",
      intro:
        "Let an agent read and update your Notion pages and databases. One pasted token is all it takes — and the agent only ever sees the pages you share with it.",
      step1:
        "In Notion, open **notion.so/profile/integrations** and click **New integration**. Pick your workspace and give it a name — say, \"My agent\".",
      step2:
        "Open the integration you just made and copy its **Internal Integration Secret** — a token starting with `ntn_`. That token is the only key you need.",
      step3:
        "Still in Notion, open each page or database the agent should see, click the **⋯** menu → **Connections**, and add your integration. Subpages come along automatically.",
      step4:
        "Here, open your agent's **Plugins** tab, enable **Notion**, and paste the token into the Notion panel. It is verified and stored encrypted right away.",
      step5:
        "Open **Chat** and try it: ask the agent what's on one of the pages you shared.",
      openAgents: "Open my agents",
    },
    telegram: {
      title: "Connect it to Telegram",
      intro:
        "Want to reach your agent from your phone? Give it its own Telegram bot. Only the people you list are allowed to talk to it.",
      step1:
        "In Telegram, open **@BotFather** and send `/newbot`. Follow the prompts to name your bot.",
      step2:
        "BotFather gives you a **token** — a long line that looks like `12345:ABC…`. Copy it.",
      step3: "Here, open your agent, find the **Telegram** section, and paste the token.",
      step4:
        "Add your own **Telegram user id** to the allowed list so you can message it. To find your id, open **@userinfobot** in Telegram — it replies with your number.",
      step5:
        "Save, then open your new bot in Telegram and send it a message. Your agent answers.",
      openAgents: "Open my agents",
    },
    schedule: {
      title: "Run it on a schedule",
      body:
        "An agent doesn't have to wait for you. Set a schedule and it runs on its own — a morning summary, a weekly report, a nightly check. Every run is saved so you can see exactly what it did.",
      cta: "Set up a schedule",
    },
  },
  landing: {
    meta: {
      title: "AI Agents — personal AI assistants with your own tools",
      description:
        "AI Agents is a platform for building personal AI assistants: define agents, connect the tools they may use through MCP servers you register, chat with them live and run them on a schedule.",
    },
    signIn: "Sign in",
    openApp: "Open app",
    getStarted: "Get started",
    hero: {
      title: "Own your agents. Connect your own tools.",
      subtitle:
        "AI Agents is a platform for building personal AI assistants. You define the agents, connect the tools they may use via MCP servers you register, chat with them live, and let them run on a schedule — with every run fully traced.",
    },
    features: {
      ownAgents: {
        title: "Your own agents",
        text: "Create agents with their own system prompts, models and tool bindings. Every agent belongs to you.",
      },
      ownTools: {
        title: "Bring your own tools",
        text: "Register remote MCP servers you control. The platform discovers their tool catalogs; you choose which tools each agent may call.",
      },
      liveChat: {
        title: "Live chat",
        text: "Talk to agents with streaming responses and watch tool calls happen in real time.",
      },
      schedules: {
        title: "Schedules",
        text: "Run agents unattended on a cron schedule — reports, checks and chores while you sleep.",
      },
      traces: {
        title: "Full traces",
        text: "Every run is recorded: iterations, token usage and a complete trace of each tool call.",
      },
      selfHost: {
        title: "Self-hostable",
        text: "FastAPI and Postgres, nothing else. Your data and credentials stay on your server, encrypted at rest.",
      },
    },
    google: {
      title: "Signing in with Google",
      body: "You can create an account with an email and password, or sign in with Google. If you sign in with Google, AI Agents receives only your Google account id, email address and name, and uses them for one purpose: to create your account and sign you in. AI Agents does not request access to your Gmail, Google Drive, Google Calendar, contacts or any other Google data, and never sells or shares your information.",
      privacyBefore: "Read the",
      privacyLink: "privacy policy",
      privacyAfter: "for everything the service stores and how it is used.",
    },
    footer: {
      copyright: "© {year} AI Agents",
      privacy: "Privacy policy",
    },
  },
  login: {
    tagline: "Own your agents. Connect your own tools.",
    tabs: { signIn: "Sign in", signUp: "Create account" },
    email: "Email",
    password: "Password",
    passwordHint: "At least 8 characters",
    codeLabel: "Verification code",
    codePlaceholder: "6-digit code",
    resend: "Resend code",
    submit: {
      wait: "Please wait…",
      signIn: "Sign in",
      verify: "Verify & create account",
      sendCode: "Send code",
      signUp: "Create account",
    },
    forgot: "Forgot password?",
    or: "or",
    toasts: {
      codeSent: "We emailed you a 6-digit verification code.",
      codeResent: "Another code is on its way.",
      resendFailed: "Could not resend the code.",
      googleFailed: "Google sign-in failed. Please try again.",
      genericError: "Something went wrong. Please try again.",
    },
  },
  acceptInvite: {
    title: "Workspace invite",
    invitedBy: "Invited by {who}",
    sentTo: "This invite was sent to {email}.",
    checking: "Checking your invite…",
    accepting: "Joining the workspace…",
    continue: "Continue",
    genericError: "Could not accept this invite.",
  },
  chat: {
    toasts: {
      loadConversationFailed: "Could not load this conversation.",
      connectionDropped: "The connection dropped. Reload to see what was saved.",
      startRunFailed: "Could not start the run.",
    },
    noAgents: {
      title: "Create an agent to start chatting",
      description:
        "An agent is a model plus instructions plus the tools you allow. You need one before you can have a conversation.",
      newHere: "New here?",
      readGuide: "Read the guide",
    },
    pickAgent: "Pick an agent",
    newConversation: "New conversation",
    noConversations: "No conversations yet.",
    untitledConversation: "Untitled",
    you: "You",
    agentFallback: "Agent",
    thisAgentFallback: "This agent",
    fallbackAgentNameLower: "your agent",
    emptyMessages: {
      title: "Say something to get started",
      withTools_one: "{name} can use {count} tool to answer you.",
      withTools_few: "{name} can use {count} tools to answer you.",
      withTools_many: "{name} can use {count} tools to answer you.",
      withTools_other: "{name} can use {count} tools to answer you.",
      noTools: "{name} has no tools yet. Connect a tool server to make it more useful.",
    },
    assistantThinking: "Thinking…",
    messagePlaceholder: "Message {name}…",
    messageAriaLabel: "Message {name}",
    stop: "Stop",
    send: "Send",
    hint: {
      busy: "Agent is working… you can keep typing",
      idle: "Enter to send · Shift+Enter for new line",
    },
    truncated: "truncated",
    emptyAssistantMessage: "(used tools without saying anything)",
    reasoning: {
      live: "Thinking…",
      label: "Thinking",
    },
    attach: "Attach image",
    removeImage: "Remove image",
    imageTooLarge: "Images must be 10 MB or smaller.",
    tooManyImages: "You can attach up to 4 images per message.",
    attachmentUploadFailed: "Could not upload the image.",
  },
  agents: {
    title: "Agents",
    description: "Each agent has its own instructions, model and set of tools.",
    newAgent: "New agent",
    empty: {
      title: "No agents yet",
      description:
        "An agent is a model, a system prompt and the tools you let it use. Create one to start chatting.",
    },
    noDescription: "No description.",
    chatAction: "Chat",
    editAria: "Edit {name}",
  },
  editor: {
    toasts: {
      loadFailed: "Could not load this agent.",
      saved: "{name} saved.",
      saveFailed: "Could not save this agent.",
      deleted: "Agent deleted.",
      deleteFailed: "Could not delete this agent.",
    },
    noProvider: {
      title: "No model provider is configured",
      description: "Set DEEPSEEK_API_KEY (or LLM_PROVIDERS) on the server and restart it.",
    },
    basics: {
      title: "Basics",
      nameLabel: "Name",
      namePlaceholder: "Support assistant",
      descriptionLabel: "Description",
      descriptionPlaceholder: "What is this agent for?",
      systemPromptLabel: "System prompt",
      systemPromptPlaceholder:
        "You are a helpful assistant. Use the available tools to answer questions about customers.",
      systemPromptHint:
        "Sent at the start of every conversation, and frozen onto each run so past runs keep their original meaning.",
      activeLabel: "Active — inactive agents cannot be run",
      webToolsLabel: "Web tools — let this agent search the web and read pages",
      responseStyleLabel: "Reply style",
      responseStyleHint: "How long and detailed the agent's replies are.",
      responseStyleConcise: "Concise",
      responseStyleExtended: "Extended",
      responseStyleDetailed: "Detailed",
    },
    model: {
      title: "Model",
      description: "Providers are configured by whoever runs this server.",
      providerLabel: "Provider",
      providerPlaceholder: "Pick a provider",
      noApiKeySuffix: " (no API key)",
      modelLabel: "Model",
      modelPlaceholder: "Pick a model",
      temperatureLabel: "Temperature",
      temperaturePlaceholder: "provider default",
      stepLimitLabel: "Step limit",
      stepLimitHint: "How many rounds of tool use before the agent must wrap up.",
    },
    toolsSection: {
      title: "Tools",
      description: "Only what you enable here is offered to the model.",
      emptyTitle: "No tool servers connected",
      emptyDescription: "Connect an MCP server first and its tools will appear here.",
      goToServers: "Go to tool servers",
      availableCount_one: "{count} tool available",
      availableCount_few: "{count} tools available",
      availableCount_many: "{count} tools available",
      availableCount_other: "{count} tools available",
      useAllLabel: "Use all",
      autoIncludeHint: "Tools added to this server later will be available automatically.",
    },
    saveFirst: {
      title: "Plugins, Telegram & memory",
      description: "Save the agent first — the Plugins, Telegram and Memory tabs appear after that.",
    },
    submit: {
      save: "Save agent",
      saving: "Saving…",
    },
    deleteButton: "Delete",
    deleteConfirm:
      "Delete {name}? Its memories, Telegram bot and schedules are removed. Past runs stay on the Runs page.",
    title: {
      new: "New agent",
      edit: "Edit agent",
    },
    description: "Give it instructions, pick a model, and choose which of your tools it may use.",
    back: "Back",
    tabs: {
      settings: "Settings",
      plugins: "Plugins",
      telegram: "Telegram",
      memory: "Memory",
    },
  },
  memory: {
    toasts: {
      loadFailed: "Could not load memories.",
      updateAgentFailed: "Could not update the agent.",
      saveFailed: "Could not save the memory.",
      editFailed: "Could not update the memory.",
      deleted: "Memory deleted.",
      deleteFailed: "Could not delete the memory.",
    },
    deleteConfirm: "Delete this memory permanently?",
    loading: "Loading memories…",
    title: "Memory",
    savedCount_one: "{count} saved fact.",
    savedCount_few: "{count} saved facts.",
    savedCount_many: "{count} saved facts.",
    savedCount_other: "{count} saved facts.",
    hintEnabled:
      "Shared across every chat with this agent, including Telegram. Newest facts are included in each run.",
    hintDisabled: "Memory is off — the agent neither sees nor saves these.",
    addPlaceholder: "Add a fact the agent should remember…",
    saveButton: "Save memory",
    searchPlaceholder: "Search memories…",
    emptyNone: "No memories yet. The agent saves facts as you talk, or add one above.",
    emptyNoMatch: "No memories match “{query}”.",
    editSaveButton: "Save",
    editCancelButton: "Cancel",
    editAria: "Edit memory",
    deleteAria: "Delete memory",
  },
  pluginsTab: {
    toasts: {
      loadFailed: "Could not load plugins.",
      enabled: "{name} enabled.",
      enableFailed: "Could not enable this plugin.",
      saved: "{name} saved.",
      saveConfigFailed: "Could not save this plugin.",
      disabled: "{name} disabled.",
      disableFailed: "Could not disable this plugin.",
      missingRequired: "Required: {fields}",
    },
    loading: "Loading plugins…",
    mailAccountSetupBadge: "Needs account setup",
    notionSetupBadge: "Needs integration token",
    telegramSetupBadge: "Needs QR sign-in",
    browseTitle: "Browse plugins",
    searchPlaceholder: "Search plugins…",
    emptyNone: "No plugins are available.",
    emptyNoMatch: "No plugins match “{query}”.",
    needsApiKeyBadge: "Needs API key",
    needsGoogleBadge: "Needs Google account",
    removedBadge: "No longer available",
    backButton: "All plugins",
    skillsTitle: "Skills",
    noSkills: "This plugin has no skills.",
    mcpServersTitle: "MCP servers",
    noExternalServers: "This plugin runs its tools inside the platform — no external servers.",
    connectionErrorTitle: "Connection error",
    configTitle: "Configuration",
    secretFieldPlaceholder: "Leave blank to keep the current value",
    saveConfigButton: "Save configuration",
    disableConfirmOAuth:
      "Disable this plugin for the agent? Its Google account is disconnected; stored data is kept.",
    disableConfirmPlain: "Disable this plugin for the agent? Its stored data is kept.",
    oauth: {
      toasts: {
        connectFailed: "Could not start the connection.",
        disconnected: "Google account disconnected.",
      },
      unavailableHint:
        "The server admin must set GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET and GOOGLE_OAUTH_REDIRECT_URI to enable Google connections.",
      title: "Google account",
      connectButton: "Connect {name}",
      reconnectBadge: "Reconnect needed",
      lostTitle: "Google access lost",
      reconnectButton: "Reconnect",
      disconnectConfirm: "Disconnect {who} from this agent?",
      accountFallback: "this Google account",
      connectPrompt: "Connect the Google account whose calendar this agent should use.",
    },
  },
  telegram: {
    toasts: {
      loadFailed: "Could not load the Telegram settings.",
      missingToken: "Paste the bot token from @BotFather first.",
      connected: "Connected as @{username}.",
      saveFailed: "Could not save.",
      disconnected: "Bot disconnected.",
    },
    invalidIds: "Not user IDs: {ids}",
    disconnectConfirm: "Disconnect this Telegram bot? The whitelist is deleted too.",
    title: "Telegram",
    description:
      "Talk to this agent from Telegram — directly or in a group (mention the bot or use /ask). Only whitelisted Telegram users are answered, wherever they write from.",
    tokenSuffix: "(token ••••{last4})",
    steps: {
      step1: "Message @BotFather, send /newbot, and copy the token.",
      step2: "Paste the token below and save.",
      step3: "Send /start to your bot; it replies with the user ID to whitelist.",
      step4:
        "For groups: in @BotFather run /setprivacy → Disable, then add the bot to the group (re-add it if it was already there) — otherwise Telegram only delivers /commands, never @mentions.",
    },
    errorTitle: "Bot error",
    tokenLabel: "Bot token",
    tokenPlaceholderKeep: "Leave blank to keep the current token",
    userIdsLabel: "Allowed user IDs",
    userIdsHint:
      "One per line. A listed user can talk to the bot anywhere — direct chat or any group. Someone not listed gets a reply with their user ID so you can paste it here.",
    submit: {
      save: "Save Telegram settings",
      connect: "Connect bot",
    },
  },
  connections: {
    title: "Tool servers",
    description: "Connect an MCP server and its tools become available to your agents.",
    addServer: "Add server",
    empty: {
      title: "No tool servers yet",
      description:
        "Agents can only use tools you connect. Add an MCP server URL and its tools will be discovered automatically.",
      action: "Add your first server",
    },
    toasts: {
      loadFailed: "Could not load tool servers.",
      reachable: "{name} is reachable.",
      catalogUpdated: "Catalog updated: {added} added, {updated} updated, {removed} removed.",
      loadToolsFailed: "Could not load the tool list.",
      removed: "{name} removed.",
      connectFailed: "Could not connect to that server.",
    },
    connectedCount_one: "Connected. Found {count} tool.",
    connectedCount_few: "Connected. Found {count} tools.",
    connectedCount_many: "Connected. Found {count} tools.",
    connectedCount_other: "Connected. Found {count} tools.",
    lastAttemptFailedTitle: "Last attempt failed",
    serverInfo: "Server: {name}{version}",
    checkedAt: "Checked {when}",
    test: "Test",
    refreshTools: "Refresh tools",
    browseTools: "Browse tools",
    removeConfirm: "Remove {name}? Agents using it will lose those tools.",
    remove: "Remove",
    noTools: "This server exposes no tools.",
    unsupported: "Unsupported",
    callsAs: "calls as {name}",
    dialog: {
      title: "Add a tool server",
      description:
        "Paste the URL of a remote MCP server. We will connect straight away and list the tools it offers.",
      namePlaceholder: "Example tools",
      urlLabel: "Server URL",
      urlHint: "Streamable HTTP or SSE — we detect which one the server speaks.",
      authLabel: "Authentication",
      authNone: "None",
      authBearer: "Bearer token",
      authHeader: "Custom header",
      authBasic: "Basic (user:password)",
      headerNameLabel: "Header name",
      secretLabel: "Secret",
      secretLabelBasic: "username:password",
      secretHint: "Encrypted before it is stored, and never shown again.",
      connect: "Connect",
      connecting: "Connecting…",
    },
  },
  mailAccounts: {
    title: "Email accounts",
    description:
      "Add as many mailboxes as this agent needs. Each uses an app password, never your main password.",
    addAccount: "Add account",
    loading: "Loading accounts…",
    empty: "No accounts yet. Until you add one, the agent's mail tools ask for it.",
    access: {
      read: { label: "Read", hint: "Search and read; can mark read or flag." },
      send: { label: "Read + send", hint: "Also compose and reply." },
      full: { label: "Full", hint: "Also move, and delete to Trash." },
    },
    status: {
      error: "Error",
    },
    testAria: "Test {email}",
    editAria: "Edit {email}",
    removeAria: "Remove {email}",
    toasts: {
      loadFailed: "Could not load mail accounts.",
      connected: "{email} connected.",
      connectFailed: "Could not connect {email}.",
      testFailed: "Could not test this account.",
      removeFailed: "Could not remove this account.",
      saveFailed: "Could not save this account.",
    },
    removeConfirm: "Remove {email} from this agent? Its saved password is deleted.",
    dialog: {
      needPassword: "Enter the app password.",
      needServers: "Choose a provider, or fill in the servers under Advanced.",
      editTitle: "Edit {email}",
      addTitle: "Add email account",
      description: "Use an app password from your provider. Saving tests the connection.",
      providerLabel: "Provider",
      providerOther: "Other (enter servers)",
      emailLabel: "Email address",
      passwordLabel: "App password",
      passwordPlaceholderPlain: "Leave blank to keep the current one",
      passwordPlaceholderLast4: "Leave blank to keep the current one (••••{last4})",
      helpLink: "How to create a {provider} app password",
      displayNameLabel: "Display name",
      labelLabel: "Label",
      labelPlaceholder: "From the address, e.g. gmail",
      accessLabel: "What the agent may do",
      advanced: "Advanced",
      imapTitle: "Incoming (IMAP)",
      smtpTitle: "Outgoing (SMTP)",
      hostAria: "{title} host",
      portAria: "{title} port",
      securityAria: "{title} security",
      usernameAria: "{title} username",
      securitySsl: "SSL/TLS",
      securityStarttls: "STARTTLS",
      usernamePlaceholder: "Username (defaults to the email address)",
      sentFolderLabel: "Sent folder",
      sentFolderPlaceholder: "Detected automatically",
      appendSentLabel: "Save a copy of sent mail to the Sent folder",
      save: "Save",
    },
  },
  notionPanel: {
    title: "Notion workspace",
    description:
      "Paste an internal-integration token. The agent only sees pages you share with the integration inside Notion.",
    loading: "Checking the connection…",
    tokenLabel: "Integration token",
    connectButton: "Connect",
    cancelButton: "Cancel",
    replaceButton: "Replace token",
    unknownWorkspace: "Notion workspace",
    tokenHint: "Token ••••{last4}",
    status: {
      ok: "Connected",
      error: "Error",
    },
    testAria: "Test the connection",
    removeAria: "Disconnect Notion",
    help: "Create an integration, share the pages it should see, then paste its token:",
    removeConfirm: "Disconnect Notion from this agent? Its saved token is deleted.",
    toasts: {
      loadFailed: "Could not load the Notion connection.",
      connected: "{workspace} connected.",
      connectFailed: "Could not connect Notion.",
      testOk: "The connection works.",
      testFailed: "Could not test the connection.",
      removeFailed: "Could not disconnect Notion.",
    },
  },
  telegramPanel: {
    title: "Telegram account",
    description:
      "Link your own Telegram account by scanning a QR code. The agent reads and sends messages as you — not as a bot.",
    loading: "Checking the connection…",
    unavailableTitle: "Telegram is not available",
    unavailableHint:
      "This server has no Telegram app credentials configured. An administrator must set TELEGRAM_PLUGIN_API_ID and TELEGRAM_PLUGIN_API_HASH.",
    connectButton: "Connect Telegram",
    reconnectButton: "Reconnect",
    cancelButton: "Cancel",
    unknownAccount: "Telegram account",
    qrStep1: "Open Telegram on your phone.",
    qrStep2: "Go to Settings → Devices → Link Desktop Device.",
    qrStep3: "Point your phone at this code.",
    qrAlt: "Telegram login QR code",
    passwordLabel: "Two-step verification password",
    passwordHint: "Your account has a 2FA password. Enter it to finish signing in.",
    submitPasswordButton: "Submit",
    status: {
      ok: "Connected",
      error: "Error",
    },
    testAria: "Test the connection",
    disconnectAria: "Disconnect Telegram",
    disconnectConfirm: "Disconnect Telegram from this agent? Its saved session is deleted.",
    toasts: {
      loadFailed: "Could not load the Telegram connection.",
      startFailed: "Could not start the Telegram sign-in.",
      loginFailed: "Telegram sign-in failed.",
      connected: "Telegram connected.",
      testOk: "The connection works.",
      testFailed: "Could not test the connection.",
      disconnectFailed: "Could not disconnect Telegram.",
    },
  },
  plugins: {
    title: "Plugins",
    description: "Bundles of skills and connected servers your agents can use.",
    toasts: {
      loadFailed: "Could not load plugins.",
    },
    empty: {
      title: "No plugins available",
      description: "Plugins are installed on the server. Ask whoever runs this deployment to add one.",
    },
    enableHint: "Enable plugins per agent in the agent editor.",
    removedBadge: "Removed",
    needsApiKeyBadge: "Needs API key",
    needsGoogleBadge: "Needs Google account",
    noSkills: "This plugin has no skills.",
  },
  schedules: {
    title: "Schedules",
    description: "Run an agent on a timetable, or once at a set time, with no one watching.",
    newSchedule: "New schedule",
    empty: {
      title: "No schedules yet",
      noAgents: "Create an agent first, then you can give it a timetable.",
      description: "Give an agent a cron expression or a date, and a prompt, and it will run on its own.",
      action: "Create a schedule",
    },
    toasts: {
      loadFailed: "Could not load schedules.",
      runFinished: "Run finished with status {status}.",
      deleted: "Schedule deleted.",
      created: "Schedule created.",
      createFailed: "Could not create the schedule.",
    },
    missedRunsTitle: "Missed runs are not replayed",
    missedRunsDescription:
      "If the worker is down, the most recent missed slot runs once when it comes back and the rest are skipped. A schedule runs at most once per slot, so an agent that sends mail cannot send a backlog of duplicates. One-time jobs that fell due meanwhile still fire, late.",
    status: {
      completed: "Completed",
      fired: "Fired",
      failed: "Failed",
      pending: "Pending",
      active: "Active",
      paused: "Paused",
      pausedReason: "Paused · {reason}",
    },
    onceBadge: "Once · {when}",
    nextRunSuffix: " · next run {when}",
    setInChat: "Set in chat",
    lastRun: "Last run {when}",
    outcome: "Outcome: {status}",
    consecutiveFailures_one: "{count} consecutive failure",
    consecutiveFailures_few: "{count} consecutive failures",
    consecutiveFailures_many: "{count} consecutive failures",
    consecutiveFailures_other: "{count} consecutive failures",
    runNow: "Run now",
    deleteConfirm: 'Delete the schedule "{name}"?',
    delete: "Delete",
    presets: {
      every15: "Every 15 minutes",
      hourly: "Hourly",
      weekdayNine: "Every weekday at 9am",
      dailyMidnight: "Daily at midnight",
      mondaysEight: "Mondays at 8am",
    },
    dialog: {
      description: "The agent will run unattended with the prompt below.",
      agentLabel: "Agent",
      agentPlaceholder: "Pick an agent",
      namePlaceholderCron: "Weekday briefing",
      namePlaceholderOnce: "Pay Ali back",
      whenLabel: "When",
      recurring: "Recurring",
      once: "Once",
      cronAria: "Cron expression",
      dateTimeAria: "Date and time",
      timezoneAria: "Time zone",
      invalidExpression: "Invalid expression.",
      nextRunsLabel: "Next runs",
      promptLabel: "Prompt",
      promptPlaceholderCron: "Check for customers with more than 5 open tickets and summarize them.",
      promptPlaceholderOnce: "Remind me that Ali owes me 500,000 UZS, due today.",
      create: "Create schedule",
    },
  },
  runs: {
    title: "Runs",
    description: {
      admin: "Every agent run across the platform, with a full record of the tools it called.",
      user: "Your agent runs. Full details are available to admins.",
    },
    filters: {
      allAgents: "All agents",
      allStatuses: "All statuses",
      allTriggers: "All triggers",
      allTelegramChats: "All Telegram chats",
    },
    trigger: {
      chat: "Chat",
      schedule: "Schedule",
      telegram: "Telegram",
    },
    empty: {
      title: "No runs yet",
      description: "Chat with an agent, or let a schedule fire, and the history shows up here.",
    },
    columns: {
      status: "Status",
      trigger: "Trigger",
      telegramChat: "Telegram chat",
      steps: "Steps",
      toolCalls: "Tool calls",
      tokens: "Tokens",
      cost: "Cost",
      duration: "Duration",
      started: "Started",
      created: "Created",
    },
    tokensTooltip: "input / output tokens",
    viewTrace: "Trace",
    toasts: {
      loadFailed: "Could not load runs.",
    },
    detail: {
      title: "Run trace",
      description: "Exactly what the agent did, recorded as it happened.",
      back: "Back to runs",
      toasts: {
        loadFailed: "Could not load this trace.",
      },
      runFailed: "Run failed",
      snapshotTitle: "Agent configuration at run time",
      snapshotHint:
        "Frozen when the run started, so later edits to the agent do not rewrite history.",
      llmCallsTitle: "LLM calls ({count})",
      noLlmCalls: "No requests recorded. Runs made before request recording existed show none.",
      memoryExtraction: "Memory extraction",
      stepIndex: "step {n}",
      summary: "summary",
      messageCount_one: "{count} message",
      messageCount_few: "{count} messages",
      messageCount_many: "{count} messages",
      messageCount_other: "{count} messages",
      requestLabel: "Request, exactly as sent to the provider",
      responseLabel: "Response, as returned by the provider",
      toolCallsTitle: "Tool calls ({count})",
      noToolCalls: "This run used no tools.",
      truncated: "truncated",
      argumentsLabel: "Arguments",
      redacted: "(redacted by connection policy)",
      resultLabel: "Result",
      noResult: "(no result recorded)",
      stepsCount_one: "{count} step",
      stepsCount_few: "{count} steps",
      stepsCount_many: "{count} steps",
      stepsCount_other: "{count} steps",
      tokensIn: "{count} in",
      tokensOut: "{count} out",
      tokensCached: "{count} cached",
      tokensTotal: "{count} total",
    },
  },
  activity: {
    title: "Activity",
    description: "Recent actions on your account.",
    toasts: {
      loadFailed: "Could not load activity.",
    },
    empty: "No activity recorded yet.",
    columns: {
      time: "Time",
      action: "Action",
      target: "Target",
      ip: "IP",
    },
    changes: {
      redacted: "changed",
    },
  },
  billing: {
    title: "Billing",
    workspaceLabel: "Billing for {name}",
    loadFailed: "Failed to load billing",
    yourPlan: "Your plan: {plan}",
    renewsOn: "Renews / resets on {date}",
    balanceTitle: "Balance",
    balanceHint: "Runs and voice transcription are charged from this balance.",
    topUpCta: "Your balance is used up. Top up your account to keep using agents.",
    tokens: "Tokens",
    agentsUsage: "Agents: {used} / {max}",
    free: "Free",
    tokenCapPeriod_one: "{formatted} token / 30 days",
    tokenCapPeriod_few: "{formatted} tokens / 30 days",
    tokenCapPeriod_many: "{formatted} tokens / 30 days",
    tokenCapPeriod_other: "{formatted} tokens / 30 days",
    planAgents_one: "{count} agent",
    planAgents_few: "{count} agents",
    planAgents_many: "{count} agents",
    planAgents_other: "{count} agents",
    upgradeTitle: "How to upgrade",
    upgradeBefore: "Pay to card",
    upgradeMiddle: ", then contact",
    upgradeAfter: "to activate your plan.",
  },
  resetPassword: {
    title: "Reset password",
    description: {
      email: "Enter your email and we'll send a code.",
      reset: "Enter the code and your new password.",
    },
    emailLabel: "Email",
    emailPlaceholder: "you@example.com",
    codeLabel: "Reset code",
    codePlaceholder: "6-digit code",
    newPasswordLabel: "New password",
    newPasswordPlaceholder: "At least 8 characters",
    submit: {
      wait: "Please wait…",
      sendCode: "Send code",
      updatePassword: "Update password",
    },
    backToSignIn: "Back to sign in",
    toasts: {
      codeSent: "If that account exists, a reset code is on its way.",
      passwordUpdated: "Password updated.",
      genericError: "Something went wrong. Please try again.",
    },
  },
  oauthCallback: {
    toasts: {
      startAgain: "Start the connection again from the agent editor.",
      cancelled: "Google connection cancelled.",
      incomplete: "Google did not complete the connection.",
      connected: "Connected as {email}.",
      connectFailed: "Could not connect Google.",
    },
    yourGoogleAccountFallback: "your Google account",
    connecting: "Connecting Google…",
  },
  workspaceSettings: {
    title: "Workspace settings",
    description: "Manage members, invites, and this workspace.",
    yourRole: "Your role",
    leave: "Leave",
    leaveConfirm: 'Leave "{name}"? You will lose access unless someone invites you back.',
    removeConfirm: "Remove {email} from this workspace?",
    deleteConfirm: 'Delete "{name}"? This permanently removes all its data. This cannot be undone.',
    lastOwnerHint: "A workspace must keep at least one owner.",
    roles: {
      owner: "Owner",
      admin: "Admin",
      member: "Member",
    },
    general: {
      title: "General",
      description: "Basic info about this workspace.",
      save: "Save",
    },
    members: {
      title: "Members",
      description: "Everyone with access to this workspace.",
      columnMember: "Member",
      columnRole: "Role",
      remove: "Remove",
    },
    invite: {
      title: "Invite someone",
      description: "Send an email invite to join this workspace.",
      emailLabel: "Email",
      roleLabel: "Role",
      send: "Send invite",
      empty: "No pending invites.",
      expires: "Expires {when}",
      revoke: "Revoke",
      linkHint: "Or copy the invite link (works even if email isn't configured):",
      copy: "Copy link",
      copied: "Copied",
      resend: "Resend",
    },
    danger: {
      title: "Danger zone",
      description: "Deleting a workspace is permanent.",
      delete: "Delete workspace",
    },
    toasts: {
      loadFailed: "Could not load workspace.",
      membersLoadFailed: "Could not load members.",
      invitesLoadFailed: "Could not load invites.",
      renamed: "Workspace renamed.",
      roleUpdated: "{email}'s role was updated.",
      left: "You left the workspace.",
      removed: "{email} was removed.",
      inviteSent: "Invited {email}.",
      inviteResent: "New invite link generated for {email}.",
      inviteFailed: "Could not send the invite.",
      inviteRevoked: "Invite revoked.",
      deleted: "Workspace deleted.",
    },
  },
  admin: {
    grant: {
      workspacesTitle: "Workspaces",
      workspacesDescription: "Grant a plan to one of this user's workspaces.",
      columnWorkspace: "Workspace",
      columnRole: "Role",
      columnPlan: "Plan",
      columnRenews: "Renews",
      button: "Grant",
      none: "This user does not belong to any workspace.",
      loadFailed: "Could not load this user's workspaces.",
      granted: "Granted {plan} to {workspace} for {days} days.",
      grantFailed: "Could not grant the plan.",
      daysInvalid: "Days must be between 1 and 3650.",
      columnBalance: "Balance",
      topUpButton: "Top up",
      toppedUp: "Added {amount} to {workspace}.",
      topUpFailed: "Could not top up the balance.",
      amountInvalid: "Amount must be between $0.01 and $10,000.",
    },
  },
}

export type Messages = typeof en
